/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* HustleEats Phase 2.F — Bus/train intercept targeting tab.
   Pick an offer, pick targeting tiers, preview reach. */
const TIER_LABELS = {
  1: "Past diners of this venue",
  2: "Postcode + cuisine match",
  3: "TransLink-direct + cuisine",
  4: "Loyalty regulars only",
};

export default function InterceptTab({ venueSlug }) {
  const [offers, setOffers] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [tiers, setTiers] = useState({ 1: true, 2: true, 3: false, 4: false });
  const [cuisineTags, setCuisineTags] = useState("italian");
  const [radius, setRadius] = useState(8);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState([]);

  const refresh = async () => {
    const [oRes, rRes] = await Promise.all([
      axios.get(`${API}/api/hustleeats/offers?venue_slug=${venueSlug}&live_now=true`),
      axios.get(`${API}/api/hustleeats/intercept/runs/${venueSlug}`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    setOffers(oRes.data || []);
    setRuns(rRes.data || []);
    if (oRes.data?.[0] && !selectedOfferId) setSelectedOfferId(oRes.data[0].id);
  };
  useEffect(() => { refresh(); }, [venueSlug]);

  const computeTargeting = async () => {
    if (!selectedOfferId || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/hustleeats/intercept/target`,
        {
          offer_id: selectedOfferId,
          tiers: Object.entries(tiers).filter(([, on]) => on).map(([k]) => Number(k)),
          cuisine_tags: cuisineTags.split(",").map((s) => s.trim()).filter(Boolean),
          radius_km_postcode: Number(radius),
          radius_m_translink: 400,
          max_recipients: 200,
        },
        { withCredentials: true },
      );
      setResult(data);
      refresh();
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ph2-tabbody">
      <h3 className="ph2-h3">Pick an offer + targeting tiers</h3>

      <label className="he-form__field">
        <span>Offer</span>
        <select
          value={selectedOfferId}
          onChange={(e) => setSelectedOfferId(e.target.value)}
          data-testid="ic-offer-select"
        >
          <option value="">Select an offer…</option>
          {offers.map((o) => {
            const left = o.max_covers - (o.claimed_covers || 0);
            const credit = (o.credit_amount_cents / 100).toFixed(2);
            return (
              <option key={o.id} value={o.id}>
                {`${o.title} · $${credit} credit · ${left} seats left`}
              </option>
            );
          })}
        </select>
      </label>

      <div className="ph2-tiers">
        {Object.entries(TIER_LABELS).map(([tier, label]) => (
          <label key={tier} className={`ph2-tier ${tiers[tier] ? "is-on" : ""}`}>
            <input
              type="checkbox"
              checked={!!tiers[tier]}
              onChange={(e) => setTiers({ ...tiers, [tier]: e.target.checked })}
              data-testid={`ic-tier-${tier}`}
            />
            <strong>Tier {tier}</strong>
            <span>{label}</span>
          </label>
        ))}
      </div>

      <div className="he-form__row">
        <label className="he-form__field">
          <span>Cuisine tags (comma-separated)</span>
          <input
            type="text"
            value={cuisineTags}
            onChange={(e) => setCuisineTags(e.target.value)}
            data-testid="ic-cuisines"
          />
        </label>
        <label className="he-form__field">
          <span>Radius (km, for tier 2)</span>
          <input
            type="number"
            min={0.5}
            max={30}
            step={0.5}
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
          />
        </label>
      </div>

      <button
        className="he-card__cta"
        onClick={computeTargeting}
        disabled={busy || !selectedOfferId}
        data-testid="ic-compute-btn"
      >
        {busy ? "Computing…" : "Preview reach"}
      </button>

      {result && !result.error && (
        <div className="ph2-result" data-testid="ic-result">
          <div className="ph2-result__counts">
            {Object.entries(result.counts_by_tier || {}).map(([t, c]) => (
              <div key={t}>
                <span>Tier {t}</span>
                <strong>{c}</strong>
              </div>
            ))}
            <div>
              <span>Total reach</span>
              <strong>{result.total_recipients}</strong>
            </div>
          </div>
          {result.recipients && result.recipients.length > 0 && (
            <details>
              <summary>Recipient sample ({Math.min(10, result.recipients.length)} of {result.total_recipients})</summary>
              <ul className="ph2-result__recipients">
                {result.recipients.slice(0, 10).map((r) => (
                  <li key={r.user_id}>
                    <strong>{r.user_id}</strong> · Tier {r.tier} · {r.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {result?.error && <div className="he-admin__msg he-admin__msg--err">{result.error}</div>}

      <h3 className="ph2-h3" style={{ marginTop: 32 }}>Past runs ({runs.length})</h3>
      {runs.length === 0 && <div className="he-state">No targeting runs yet.</div>}
      {runs.map((r) => (
        <div key={r.id} className="ph2-event" data-testid={`ic-run-${r.id}`}>
          <div>
            <div className="ph2-event__name">{r.recipient_count} recipients · tiers {r.tiers.join(", ")}</div>
            <div className="ph2-event__sub">{new Date(r.created_at).toLocaleString("en-AU")} · offer {r.offer_id}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
